import MessageBanner from 'LuxKit/Banners/MessageBanner'
import { urlHashDatePicker } from 'constants/url'
import { EmptyObject } from 'lib/object/objectUtils'
import offerPageURL from 'lib/offer/offerPageURL'
import { buildSearchParamsFromFilters } from 'lib/search/searchUtils'
import React, { useMemo } from 'react'
import { OfferUnavailableReason } from 'lib/search/constants'
import { UNAVAILABLE_BANNER_MESSAGE_STAY_MIN_NIGHTS } from 'components/OfferList/OfferListTiles/constants'
import { dateDifference } from 'lib/datetime/dateUtils'

interface Props {
  offer: App.HotelOffer | App.HotelOfferSummary | App.BundleOffer | App.VillaOffer | App.VillaOfferSummary
  unavailableReason: string | undefined
  filters?: App.OfferListFilters
}

function HotelSearchTileUnavailabilityBanner({
  offer,
  unavailableReason,
  filters = EmptyObject,
}: Props) {
  const checkIn = filters?.checkIn
  const checkOut = filters?.checkOut
  const duration = useMemo(() => {
    if (checkIn && checkOut) {
      return dateDifference(new Date(checkOut), new Date(checkIn)).days
    }
    return undefined
  }, [checkIn, checkOut])

  const bannerMessage = useMemo(() => {
    if (offer.isSoldOut) return 'Sold out for your dates'
    if (unavailableReason === OfferUnavailableReason.FLIGHT_CLASS_UNAVAILABLE) {
      return 'Selected flight class is not available for your dates'
    }
    if (unavailableReason === OfferUnavailableReason.CAPACITY_EXCEEDED) {
      return 'Unavailable for the number of guests selected'
    }
    if (unavailableReason === OfferUnavailableReason.UNAVAILABLE_FOR_FILTERS_SELECTED) {
      return 'Unavailable for your filters'
    }

    if (duration &&
      offer.minDuration &&
      offer.minDuration > duration) {
      return UNAVAILABLE_BANNER_MESSAGE_STAY_MIN_NIGHTS(offer.minDuration)
    }

    return 'Unavailable for your dates'
  }, [duration, offer.isSoldOut, offer.minDuration, unavailableReason])

  const offerUrl = useMemo(() => {
    const searchParams = buildSearchParamsFromFilters({
      ...filters,
      checkIn: undefined,
      checkOut: undefined,
    })

    if (unavailableReason === OfferUnavailableReason.CAPACITY_EXCEEDED) return offerPageURL(offer, searchParams)

    return offerPageURL(offer, searchParams) + urlHashDatePicker
  }, [filters, offer, unavailableReason])

  return (
    <MessageBanner
      kind="informational-primary"
      primaryActionButtonProps={{
        children:
          unavailableReason === OfferUnavailableReason.CAPACITY_EXCEEDED || unavailableReason === OfferUnavailableReason.UNAVAILABLE_FOR_FILTERS_SELECTED ? 'View offer' : 'View available dates',
        to: offerUrl,
      }}
    >
      <b>{bannerMessage}</b>
    </MessageBanner>
  )
}

export default HotelSearchTileUnavailabilityBanner
